import { useEffect, useMemo, useState } from 'react';

import MultiSelectVehicleGroupsSidebarOverlay from './MultiSelectVehicleGroupsSidebarOverlay';

import type { VehicleGroup } from '@cobliteam/common-types';

import type { UserPreferences } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import { useUser, useVehicleGroup } from '@cobliteam/hooks';
import { useGlobalFilterStore } from '@cobliteam/hooks';

import Dropdown from '@cobliteam/ui/components/Dropdown';
import { SidebarButton } from './components/SidebarButton';

export interface MultiSelectVehicleGroupsSidebarProps {
  hideEmptyGroups?: boolean;
  userPreferences?: UserPreferences;
  isSidebarOpen: boolean;
}

const MultiSelectVehicleGroupsSidebar = ({ hideEmptyGroups, userPreferences, isSidebarOpen }: MultiSelectVehicleGroupsSidebarProps) => {
  const { getVehicleGroups } = useVehicleGroup();
  const { data: vehicleGroups = [], isFetching, isLoading } = getVehicleGroups();
  const { getUser, updatePreferences, createPreferences } = useUser();
  const user = getUser();

  const setVehicleGroupIds = useGlobalFilterStore((state) => state.setVehicleGroupIds);

  const [vehicleGroupIdsPrefs, setVehicleGroupIdsPrefs] = useState(userPreferences?.vehicleGroupIds || localStorage?.vehicleGroupIds || []);
  const selectedVehicleGroups = useMemo(() => vehicleGroups.filter((group) => vehicleGroupIdsPrefs.includes(group.id)), [vehicleGroups, vehicleGroupIdsPrefs]);
  const [selectedGroupsState, setSelectedGroupsState] = useState<VehicleGroup[]>(selectedVehicleGroups);
  const filteredGroups = hideEmptyGroups ? vehicleGroups?.filter((group) => Boolean(group.vehicleIds?.length)) : vehicleGroups;

  useEffect(() => {
    setSelectedGroupsState(selectedVehicleGroups);
  }, [selectedVehicleGroups]);

  const [forceClosed, setForceClosed] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [closedByApply, setClosedByApply] = useState(false);
  const [selectionChanged, setSelectionChanged] = useState(false);

  useEffect(() => {
    if (!isDropdownOpen && !closedByApply) {
      setSelectedGroupsState(selectedVehicleGroups);
      setSelectionChanged(false);
    }
    setClosedByApply(false);
  }, [isDropdownOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  const verifySomethingChanges = (groupsToBeApplied: VehicleGroup[]) => {
    const hasNotChanged =
      groupsToBeApplied.length === selectedGroupsState.length && groupsToBeApplied.every((group) => selectedGroupsState.some((selectedGroup) => selectedGroup.id === group.id));
    setSelectionChanged(!hasNotChanged);
  };

  const onChangeSelect = (group: VehicleGroup) => {
    const isSelected = selectedGroupsState.find((selectedGroup) => selectedGroup.id === group.id);

    const updatedSelected = isSelected ? selectedGroupsState.filter((selectedGroup) => selectedGroup.id !== group.id) : [...selectedGroupsState, group];

    setSelectedGroupsState(updatedSelected);
    verifySomethingChanges(updatedSelected);
  };

  const clearFilters = () => {
    setSelectedGroupsState([]);
    verifySomethingChanges([]);
  };

  const cancel = () => {
    setForceClosed((prev) => !prev);
    setSelectedGroupsState(selectedVehicleGroups);
    setSelectionChanged(false);
  };

  const vehicleGroupToId = (vehicle: VehicleGroup[]) => {
    return vehicle.map((group) => group.id);
  };

  const updateOrCreatePreferences = () => {
    if (userPreferences?.id) {
      updatePreferences.mutate(
        {
          userId: user.id,
          preferencesToUpdate: {
            id: userPreferences.id,
            vehicleGroupIds: vehicleGroupToId(selectedGroupsState),
            useVehiclesCluster: userPreferences.useVehiclesCluster,
          },
        },
        {
          onSuccess: () => {
            setVehicleGroupIds(vehicleGroupToId(selectedGroupsState));
            setVehicleGroupIdsPrefs(vehicleGroupToId(selectedGroupsState));
          },
        }
      );
    } else {
      createPreferences.mutate(
        {
          userId: user.id,
          preferencesToSave: {
            vehicleGroupIds: vehicleGroupToId(selectedGroupsState),
            useVehiclesCluster: false,
          },
        },
        {
          onSuccess: () => {
            setVehicleGroupIds(vehicleGroupToId(selectedGroupsState));
            setVehicleGroupIdsPrefs(vehicleGroupToId(selectedGroupsState));
          },
        }
      );
    }
  };

  const onApplyFilter = () => {
    setClosedByApply(true);

    updateOrCreatePreferences();

    setForceClosed(true);
    setSelectionChanged(false);
    Analytics.track('Global Filter Applied');
  };

  const onOpenChange = () => {
    setForceClosed(false);
    setIsDropdownOpen((prev) => !prev);
  };

  return (
    <div id='sidebar-global-filter-id' className='mt-4 mb-2 flex w-full items-center justify-center [&>.active]:bg-transparent'>
      <Dropdown
        onOpenChange={onOpenChange}
        forceClose={forceClosed}
        placement={isSidebarOpen ? 'bottom' : 'right-end'}
        overlay={
          <MultiSelectVehicleGroupsSidebarOverlay
            isLoading={isFetching || isLoading}
            vehicleGroups={filteredGroups}
            selectedGroups={selectedGroupsState}
            selectionChanged={selectionChanged}
            onChange={onChangeSelect}
            onClearFilter={clearFilters}
            onApplyFilter={onApplyFilter}
            onCancel={cancel}
          />
        }
      >
        <div data-testid='multiselect-vehicle-groups-sidebar'>
          <SidebarButton isSidebarOpen={isSidebarOpen} totalSelectedGroups={selectedGroupsState.length} />
        </div>
      </Dropdown>
    </div>
  );
};

MultiSelectVehicleGroupsSidebar.displayName = 'MultiSelectVehicleGroupsSidebar';

export default MultiSelectVehicleGroupsSidebar;
